import type {
  AccessCatalogItemPublic,
  AccessRecordPublic,
  ApplicationPublic,
  GrantPublic,
} from '@accounts/shared';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { STR } from '../../../../i18n';
import {
  ApiError,
  listAccessCatalog,
  listAccessLevels,
  listAccessNodes,
  listAccessRecords,
  listApplications,
  listGrants,
  listUsers,
} from '../../../../lib/api';
import { queryKeys } from '../../../../lib/queryKeys';

/**
 * The reads the Catalog and the Matrix share, each under the ONE key its kind uses
 * everywhere — so a decision on the Queue or an edit on the person page refreshes these
 * through `accessAll`, and the users and applications reads are the very cache entries the
 * Users and Applications screens already fill.
 *
 * The admin screens read retired nodes and rows too (`includeRetired`): a retired entry
 * stays visible, faded, with its history — retire, never delete — and the pickers filter
 * it out themselves.
 */

/** Same page size as every other admin list; the server's own cap. */
const LIST_LIMIT = 200;

export function useAccessLevels() {
  return useQuery({
    queryKey: queryKeys.accessLevels,
    queryFn: () => listAccessLevels(),
    select: (response) => [...response.data].sort((a, b) => a.depth - b.depth),
  });
}

export function useAccessNodesAll() {
  return useQuery({
    queryKey: queryKeys.accessNodes(true),
    queryFn: () => listAccessNodes({ includeRetired: true }),
    select: (response) => response.data,
  });
}

export function useAccessCatalogAll() {
  return useQuery({
    queryKey: queryKeys.accessCatalog(true),
    queryFn: () => listAccessCatalog({ includeRetired: true }),
    select: (response) => response.data,
  });
}

export interface RecordsRead {
  /** Undefined until every row's read has landed. */
  data: AccessRecordPublic[] | undefined;
  isError: boolean;
  refetch: () => void;
}

/**
 * Every live record on the outside-system rows (removed ones excluded, as the server does
 * by default). The records list takes EXACTLY ONE scope — a person or a catalog row, never
 * an unbounded dump — so this is one read per outside row, each under the same key the row
 * drawer and the person page use, combined here into one answer.
 */
export function useAccessRecordsAll(items: readonly AccessCatalogItemPublic[]): RecordsRead {
  const outsideIds = items.filter((item) => item.application === null).map((item) => item.id);
  return useQueries({
    queries: outsideIds.map((catalogItemId) => ({
      queryKey: queryKeys.accessRecords({ catalogItemId, includeRemoved: false }),
      queryFn: () => listAccessRecords({ catalogItemId }),
    })),
    combine: (results): RecordsRead => ({
      data: results.every((result) => result.data !== undefined)
        ? results.flatMap((result) => result.data?.data ?? [])
        : undefined,
      isError: results.some((result) => result.isError),
      refetch: () => {
        for (const result of results) void result.refetch();
      },
    }),
  });
}

export interface GrantsRead {
  /** Undefined until every application's read has landed. */
  data: GrantPublic[] | undefined;
  isError: boolean;
  refetch: () => void;
}

/**
 * Every live grant, for the Matrix's Suite apps columns. The grants list takes a person or
 * an application, never neither, so this is one read per application, each under the key
 * the Catalog's app-row drawer already reads.
 */
export function useLiveGrantsAll(applications: readonly ApplicationPublic[]): GrantsRead {
  return useQueries({
    queries: applications.map((application) => ({
      queryKey: queryKeys.grantsForApplication(application.id, false),
      queryFn: () => listGrants({ applicationId: application.id, includeRevoked: false }),
    })),
    combine: (results): GrantsRead => ({
      data: results.every((result) => result.data !== undefined)
        ? results.flatMap((result) => result.data?.data ?? [])
        : undefined,
      isError: results.some((result) => result.isError),
      refetch: () => {
        for (const result of results) void result.refetch();
      },
    }),
  });
}

/** `enabled: false` for a session that may not read applications (HR). */
export function useApplicationsList(enabled = true) {
  return useQuery({
    queryKey: queryKeys.applications,
    queryFn: () => listApplications({ limit: LIST_LIMIT }),
    enabled,
  });
}

export function useUsersList() {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: () => listUsers({ limit: LIST_LIMIT }),
  });
}

/** After ANY access write: every access view could be showing what just changed. */
export function useInvalidateAccess(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.accessAll }),
    [queryClient],
  );
}

/** The server's own sentence when it sent one; the generic fallback otherwise. */
export function errorText(error: unknown): string {
  return error instanceof ApiError ? error.message : STR.errors.generic;
}
